import PropTypes from 'prop-types';
import { forwardRef, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// third party libraries
import { useTranslation } from 'react-i18next';

// project import
import { activeCollapseItem, activeItem } from 'store/reducers/menu';
import NavGroup from './NavGroup';
import { Collapse } from '../../../../../../node_modules/@mui/material/index';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

const NavItemCollapse = ({ item, level }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [collapseOpen, setCollapseOpen] = useState(false);
  const { t } = useTranslation();

  const { drawerOpen, openCollapseItem } = useSelector((state) => state.menu);

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  let listItemProps = { component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />) };
  if (item?.external) {
    listItemProps = { component: 'a', href: item.url, target: itemTarget };
  }

  const itemHandler = (id) => {
    dispatch(activeItem({ openItem: [''] }));
    dispatch(activeCollapseItem({ openCollapseItem: [id] }));
    if (isSelected) {
      dispatch(activeCollapseItem({ openCollapseItem: [''] }));
    }
    handleCollapse();
  };

  const Icon = item.icon;
  const itemIcon = item.icon ? <Icon style={{ fontSize: drawerOpen ? '1.25rem' : '1.25rem' }} /> : false;

  const isSelected = openCollapseItem.findIndex((id) => id === item.id) > -1;
  // active menu item on page load
  useEffect(() => {
    // if (pathname.includes(item.url)) {
    //   dispatch(activeItem({ openCollapseItem: [item.id] }));
    // }

    if (pathname.includes(item.url)) {
      dispatch(activeCollapseItem({ openCollapseItem: [item.id] }));
    }
    // eslint-disable-next-line
  }, [dispatch, pathname]);

  const textColor = 'text.primary';
  // const iconSelectedColor = 'primary.main';
  const iconSelectedColor = theme.palette.primary[400];

  const handleCollapse = () => {
    setCollapseOpen(!collapseOpen);
  };

  return (
    <>
      <ListItemButton
        // {...listItemProps}
        disabled={item.disabled}
        onClick={() => itemHandler(item.id)}
        selected={isSelected}
        sx={{
          zIndex: 1201,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pl: drawerOpen ? `${level * 28}px` : 1.5,
          py: !drawerOpen && level === 1 ? 1.25 : 1.25,
          ...(drawerOpen && {
            '&:hover': {
              bgcolor: 'secondary.light'
            },
            '&.Mui-selected': {
              borderRight: `2px solid`,
              borderColor: iconSelectedColor,
              bgcolor: 'secondary.lighter',
              color: iconSelectedColor,
              '&:hover': {
                color: iconSelectedColor,
                bgcolor: 'secondary.lighter'
              }
            }
          }),
          ...(!drawerOpen && {
            '&:hover': {
              bgcolor: 'transparent'
            },
            '&.Mui-selected': {
              '&:hover': {
                bgcolor: 'transparent'
              },
              bgcolor: 'transparent'
            }
          })
        }}
      >
        {itemIcon && (
          <ListItemIcon
            sx={{
              minWidth: 28,
              color: isSelected ? iconSelectedColor : textColor,
              ...(!drawerOpen && {
                borderRadius: 1.5,
                width: 28,
                height: 28,
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  bgcolor: 'secondary.lighter'
                }
              }),
              ...(!drawerOpen &&
                isSelected && {
                  bgcolor: 'primary.lighter',
                  '&:hover': {
                    bgcolor: 'primary.lighter'
                  }
                })
            }}
          >
            {itemIcon}
          </ListItemIcon>
        )}
        {(drawerOpen || (!drawerOpen && level !== 1)) && (
          <ListItemText
            primary={
              <Typography
                variant="h6"
                sx={{ color: isSelected ? iconSelectedColor : textColor, fontSize: '0.80rem', lineHeight: '1.563', fontWeight: 500 }}
              >
                {t(item.title)}
              </Typography>
            }
          />
        )}
        {/* {(drawerOpen || (!drawerOpen && level!== 1)) && item.subItems && (
        <NavItemCollapse key={item.id} item={item} level={level + 1} />
      )} */}
        {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
          <Chip
            color={item.chip.color}
            variant={item.chip.variant}
            size={item.chip.size}
            label={item.chip.label}
            avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
          />
        )}
        {(drawerOpen || (drawerOpen && level !== 1)) && (
          <ListItemIcon
            sx={{
              minWidth: 28,
              color: isSelected ? iconSelectedColor : textColor,
              ...(!drawerOpen && {
                borderRadius: 1.5,
                width: 36,
                height: 36,
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  bgcolor: 'secondary.lighter'
                }
              })
            }}
          >
            <ExpandMoreIcon
              fontSize="small"
              className={`transition-transform duration-300 ${isSelected ? 'transform rotate-180' : 'transform rotate-0'}`}
              sx={{
                fontWeight: '100'
              }}
            />
          </ListItemIcon>
        )}
      </ListItemButton>
      {(drawerOpen || (!drawerOpen && level !== 1)) && item.children && (
        <Collapse in={isSelected}>
          <NavGroup item={item} level={level + 1} />
        </Collapse>
      )}
    </>
  );
};

NavItemCollapse.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};

export default NavItemCollapse;
